import React, { useState, useMemo } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    ToggleFavorite,
    ToggleArchive,
    DeleteSuggestion,
    CopyTrackToClipboard,
    OpenSpotifySearch,
    OpenYouTubeSearch,
    FormatDateDisplay
} from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Panel Izquierdo: Buzón de moderación y tabla de sugerencias de aficionados y cabina.
 * 
 * @component PlaylistSuggestionsTable
 * @param {Object} props
 * @param {Array} props.suggestions - Lista de sugerencias recibidas.
 * @param {boolean} props.isLoading - Estado de carga.
 * @param {boolean} props.groupSimilar - Bandera de agrupación por similitud fonética/título.
 * @param {Function} props.onToggleGroupSimilar - Callback al alternar agrupación.
 * @param {Function} props.onEdit - Callback al pulsar editar (abre PlaylistEditSongModal).
 * @param {Function} props.onRefresh - Callback para recargar los datos.
 */
export const PlaylistSuggestionsTable = ({
    suggestions = [],
    isLoading = false,
    groupSimilar = false,
    onToggleGroupSimilar,
    onEdit,
    onRefresh
}) => {
    const { showMessage } = useFloatMessage();

    // Filtros locales
    const [searchTerm, setSearchTerm] = useState('');
    const [pillFilter, setPillFilter] = useState('all'); // 'all', 'songs', 'artists', 'manual', 'archived'
    const [sortBy, setSortBy] = useState('votes'); // 'votes', 'recent', 'alphabetical'

    // Modal Confirm
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: null,
        textYesButton: 'Sí, continuar',
        textNoButton: 'Cancelar'
    });

    // Toggle Favorite (agregar a playlist activa)
    const handleToggleFavorite = async (song) => {
        try {
            const nextState = !song.isFavorite;
            const res = await ToggleFavorite(song.id, nextState);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextState
                        ? `⭐ "${song.title}" añadida a la Playlist Activa.`
                        : `"${song.title}" retirada de la Playlist Activa.`,
                    message: nextState
                        ? `⭐ "${song.title}" añadida a la Playlist Activa.`
                        : `"${song.title}" retirada de la Playlist Activa.`
                });
                if (onRefresh) onRefresh();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al alternar favorita.',
                    message: (res && res.message) || 'Error al alternar favorita.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al actualizar estado favorito.',
                message: 'Error de red al actualizar estado favorito.'
            });
        }
    };

    // Toggle Archive con confirmación
    const handleToggleArchive = (song) => {
        const isArchiving = !song.isArchived;
        setConfirmConfig({
            message: isArchiving
                ? `¿Deseas archivar la sugerencia "${song.title}"? Se ocultará de la lista de sugerencias activas.`
                : `¿Deseas desarchivar y restaurar "${song.title}" a la moderación activa?`,
            textYesButton: isArchiving ? 'Archivar' : 'Restaurar',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await ToggleArchive(song.id, isArchiving);
                if (res && res.success) {
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: isArchiving ? 'Sugerencia archivada.' : 'Sugerencia restaurada.',
                        message: isArchiving ? 'Sugerencia archivada.' : 'Sugerencia restaurada.'
                    });
                    if (onRefresh) onRefresh();
                    return { success: true };
                }
                return { success: false, message: (res && res.message) || 'Error al archivar sugerencia.' };
            }
        });
        setShowConfirm(true);
    };

    // Delete con confirmación
    const handleDelete = (song) => {
        setConfirmConfig({
            message: `¿Estás seguro de eliminar permanentemente "${song.title}"? Esta acción no se puede deshacer.`,
            textYesButton: 'Eliminar',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await DeleteSuggestion(song.id);
                if (res && res.success) {
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: 'Sugerencia eliminada con éxito.',
                        message: 'Sugerencia eliminada con éxito.'
                    });
                    if (onRefresh) onRefresh();
                    return { success: true };
                }
                return { success: false, message: (res && res.message) || 'Error al eliminar sugerencia.' };
            }
        });
        setShowConfirm(true);
    };

    // Copiar pista al portapapeles
    const handleCopy = async (title, artist) => {
        const res = await CopyTrackToClipboard(title, artist);
        if (res.success) {
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: res.message,
                message: res.message
            });
        } else {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: res.message,
                message: res.message
            });
        }
    };

    // Filtrado y Ordenamiento
    const processedSuggestions = useMemo(() => {
        let list = [...suggestions];

        // 1. Filtro por pills
        if (pillFilter === 'archived') {
            list = list.filter(s => s.isArchived);
        } else {
            list = list.filter(s => !s.isArchived);

            if (pillFilter === 'songs') {
                list = list.filter(s => Number(s.suggestionType) === 1 || !s.suggestionType);
            } else if (pillFilter === 'artists') {
                list = list.filter(s => Number(s.suggestionType) === 2);
            } else if (pillFilter === 'manual') {
                list = list.filter(s => s.isManualEntry);
            }
        }

        // 2. Filtro por texto en vivo
        if (searchTerm.trim()) {
            const query = searchTerm.toLowerCase().trim();
            list = list.filter(s =>
                (s.title && s.title.toLowerCase().includes(query)) ||
                (s.artistName && s.artistName.toLowerCase().includes(query)) ||
                (s.fanName && s.fanName.toLowerCase().includes(query)) ||
                (s.createdByOperatorName && s.createdByOperatorName.toLowerCase().includes(query))
            );
        }

        // 3. Ordenamiento multidireccional
        list.sort((a, b) => {
            if (sortBy === 'votes') {
                // Más pedidas primero
                const diff = (b.voteCount || 0) - (a.voteCount || 0);
                if (diff !== 0) return diff;
                return new Date(b.createdAtUtc || 0) - new Date(a.createdAtUtc || 0);
            }
            if (sortBy === 'recent') {
                // Más recientes primero
                return new Date(b.createdAtUtc || 0) - new Date(a.createdAtUtc || 0);
            }
            if (sortBy === 'alphabetical') {
                // Título A-Z
                return (a.title || '').localeCompare(b.title || '', 'es', { sensitivity: 'base' });
            }
            return 0;
        });

        return list;
    }, [suggestions, pillFilter, searchTerm, sortBy]);

    // Conteo por categorías para badges de los pills
    const counts = useMemo(() => {
        const nonArchived = suggestions.filter(s => !s.isArchived);
        return {
            all: nonArchived.length,
            songs: nonArchived.filter(s => Number(s.suggestionType) === 1 || !s.suggestionType).length,
            artists: nonArchived.filter(s => Number(s.suggestionType) === 2).length,
            manual: nonArchived.filter(s => s.isManualEntry).length,
            archived: suggestions.filter(s => s.isArchived).length
        };
    }, [suggestions]);

    return (
        <section className="PlaylistSuggestionsTable">
            {/* Header del Panel */}
            <div className="PlaylistSuggestionsTable__Header">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="PlaylistSuggestionsTable__Icon">
                        <i className="bi bi-inbox-fill"></i>
                    </span>
                    <div>
                        <h3 className="PlaylistSuggestionsTable__Title mb-0">
                            Buzón de Sugerencias
                            <span className="PlaylistSuggestionsTable__BadgeCount ms-2">
                                {processedSuggestions.length}
                            </span>
                        </h3>
                        <p className="PlaylistSuggestionsTable__Subtitle mb-0">
                            {groupSimilar ? 'Vista consolidada por canción' : 'Vista detallada de envíos'}
                        </p>
                    </div>
                </div>

                {/* Switch Conmutador "Mostrar Todo" vs "Agrupar por Canción" */}
                <div className="PlaylistSuggestionsTable__GroupSwitch form-check form-switch d-flex align-items-center gap-2 mb-0">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="groupSimilarSwitch"
                        checked={groupSimilar}
                        onChange={(e) => onToggleGroupSimilar && onToggleGroupSimilar(e.target.checked)}
                    />
                    <label className="form-check-label small fw-bold text-white-50" htmlFor="groupSimilarSwitch">
                        {groupSimilar ? '🔗 Agrupado' : '📑 Mostrar Todo'}
                    </label>
                </div>
            </div>

            {/* Barra de Filtros, Búsqueda y Ordenamiento */}
            <div className="PlaylistSuggestionsTable__Controls">
                {/* Buscador */}
                <div className="PlaylistSuggestionsTable__SearchWrapper">
                    <i className="bi bi-search PlaylistSuggestionsTable__SearchIcon"></i>
                    <input
                        type="text"
                        className="PlaylistSuggestionsTable__SearchInput"
                        placeholder="Buscar por título, artista o aficionado..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="PlaylistSuggestionsTable__SearchClear"
                            onClick={() => setSearchTerm('')}
                            title="Limpiar búsqueda"
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    )}
                </div>

                {/* Ordenamiento Multidireccional */}
                <div className="PlaylistSuggestionsTable__SortWrapper">
                    <span className="PlaylistSuggestionsTable__SortLabel">
                        <i className="bi bi-sort-down me-1"></i> Orden:
                    </span>
                    <select
                        className="PlaylistSuggestionsTable__SortSelect"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        aria-label="Criterio de ordenamiento"
                    >
                        <option value="votes">🔥 Más pedidas</option>
                        <option value="recent">⏱️ Más recientes</option>
                        <option value="alphabetical">🔤 Título A-Z</option>
                    </select>
                </div>
            </div>

            {/* Pills de Filtrado Rápido */}
            <div className="PlaylistSuggestionsTable__PillsWrapper">
                <button
                    type="button"
                    className={`PlaylistSuggestionsTable__Pill ${pillFilter === 'all' ? 'PlaylistSuggestionsTable__Pill--active' : ''}`}
                    onClick={() => setPillFilter('all')}
                >
                    Todas <span className="PlaylistSuggestionsTable__PillBadge">{counts.all}</span>
                </button>
                <button
                    type="button"
                    className={`PlaylistSuggestionsTable__Pill ${pillFilter === 'songs' ? 'PlaylistSuggestionsTable__Pill--active' : ''}`}
                    onClick={() => setPillFilter('songs')}
                >
                    🎵 Canciones <span className="PlaylistSuggestionsTable__PillBadge">{counts.songs}</span>
                </button>
                <button
                    type="button"
                    className={`PlaylistSuggestionsTable__Pill ${pillFilter === 'artists' ? 'PlaylistSuggestionsTable__Pill--active' : ''}`}
                    onClick={() => setPillFilter('artists')}
                >
                    🎤 Artistas <span className="PlaylistSuggestionsTable__PillBadge">{counts.artists}</span>
                </button>
                <button
                    type="button"
                    className={`PlaylistSuggestionsTable__Pill ${pillFilter === 'manual' ? 'PlaylistSuggestionsTable__Pill--active' : ''}`}
                    onClick={() => setPillFilter('manual')}
                >
                    🎧 Manuales <span className="PlaylistSuggestionsTable__PillBadge">{counts.manual}</span>
                </button>
                <button
                    type="button"
                    className={`PlaylistSuggestionsTable__Pill PlaylistSuggestionsTable__Pill--archive ${pillFilter === 'archived' ? 'PlaylistSuggestionsTable__Pill--active' : ''}`}
                    onClick={() => setPillFilter('archived')}
                >
                    📦 Archivadas <span className="PlaylistSuggestionsTable__PillBadge">{counts.archived}</span>
                </button>
            </div>

            {/* Listado de Sugerencias */}
            <div className="PlaylistSuggestionsTable__List">
                {isLoading ? (
                    <div className="PlaylistSuggestionsTable__EmptyState">
                        <div className="spinner-border text-danger mb-2" role="status"></div>
                        <span>Cargando sugerencias de cabina...</span>
                    </div>
                ) : processedSuggestions.length === 0 ? (
                    <div className="PlaylistSuggestionsTable__EmptyState">
                        <i className="bi bi-music-note-slash display-5 d-block mb-2 text-white-50"></i>
                        <p className="mb-1 fw-bold">No hay sugerencias con este criterio de filtro.</p>
                        <span className="small text-white-50">
                            {searchTerm ? 'Prueba ajustando el término de búsqueda.' : 'Los envíos de aficionados aparecerán aquí en vivo.'}
                        </span>
                    </div>
                ) : (
                    processedSuggestions.map((song) => {
                        const isArtistType = Number(song.suggestionType) === 2;
                        const votes = song.voteCount || 0;

                        return (
                            <div
                                key={song.id}
                                className={`PlaylistSuggestionsTable__Item ${
                                    song.isFavorite
                                        ? 'PlaylistSuggestionsTable__Item--favorite'
                                        : song.isArchived
                                        ? 'PlaylistSuggestionsTable__Item--archived'
                                        : ''
                                }`}
                            >
                                {/* Bloque Izquierdo: Votos */}
                                <div className="PlaylistSuggestionsTable__VoteBox" title={`${votes} votos acumulados`}>
                                    <span className="PlaylistSuggestionsTable__VoteCount">🔥 {votes}</span>
                                    <span className="PlaylistSuggestionsTable__VoteLabel">votos</span>
                                </div>

                                {/* Bloque Central: Datos de la pista */}
                                <div className="PlaylistSuggestionsTable__Info">
                                    <div className="PlaylistSuggestionsTable__TitleRow">
                                        <span className="PlaylistSuggestionsTable__ItemTitle" title={song.title}>
                                            {song.title}
                                        </span>

                                        {/* Badges de Tipo */}
                                        {isArtistType ? (
                                            <span className="PlaylistSuggestionsTable__Badge PlaylistSuggestionsTable__Badge--artist">
                                                <i className="bi bi-person-fill me-1"></i> Artista
                                            </span>
                                        ) : (
                                            <span className="PlaylistSuggestionsTable__Badge PlaylistSuggestionsTable__Badge--song">
                                                <i className="bi bi-music-note me-1"></i> Canción
                                            </span>
                                        )}

                                        {song.isManualEntry && (
                                            <span className="PlaylistSuggestionsTable__Badge PlaylistSuggestionsTable__Badge--manual">
                                                <i className="bi bi-headset me-1"></i> Manual
                                            </span>
                                        )}

                                        {song.isFavorite && (
                                            <span className="PlaylistSuggestionsTable__Badge PlaylistSuggestionsTable__Badge--active">
                                                <i className="bi bi-star-fill me-1"></i> En Playlist
                                            </span>
                                        )}
                                    </div>

                                    {/* Metadatos secundarios */}
                                    <div className="PlaylistSuggestionsTable__MetaRow">
                                        {song.artistName && (
                                            <span className="PlaylistSuggestionsTable__Artist">
                                                <strong>{song.artistName}</strong>
                                            </span>
                                        )}

                                        <span className="PlaylistSuggestionsTable__Fan">
                                            <i className="bi bi-person-circle me-1"></i>
                                            {song.isManualEntry
                                                ? (song.createdByOperatorName || 'Operador DJ')
                                                : (song.fanName || 'Aficionado anónimo')}
                                        </span>

                                        <span className="PlaylistSuggestionsTable__Timestamp">
                                            <i className="bi bi-clock me-1"></i>
                                            {FormatDateDisplay(song.createdAtUtc)}
                                        </span>
                                    </div>

                                    {/* Lista desplegable de votantes si hay más de 1 */}
                                    {song.votes && song.votes.length > 1 && (
                                        <div className="PlaylistSuggestionsTable__VotersPreview">
                                            <i className="bi bi-people-fill me-1 text-warning"></i>
                                            Pedida también por: {song.votes.map(v => v.fanName || 'Aficionado').filter((v, i, a) => a.indexOf(v) === i).slice(0, 3).join(', ')}
                                            {song.votes.length > 3 && ` y ${song.votes.length - 3} más...`}
                                        </div>
                                    )}
                                </div>

                                {/* Bloque Derecho: Acciones de Cabina */}
                                <div className="PlaylistSuggestionsTable__Actions">
                                    {/* DJ Tools Rápidas */}
                                    <button
                                        type="button"
                                        className="PlaylistSuggestionsTable__ActionBtn"
                                        title="Copiar texto para software DJ"
                                        onClick={() => handleCopy(song.title, song.artistName)}
                                    >
                                        <i className="bi bi-clipboard"></i>
                                    </button>

                                    <button
                                        type="button"
                                        className="PlaylistSuggestionsTable__ActionBtn PlaylistSuggestionsTable__ActionBtn--spotify"
                                        title="Buscar pista en Spotify"
                                        onClick={() => OpenSpotifySearch(song.title, song.artistName)}
                                    >
                                        <i className="bi bi-spotify"></i>
                                    </button>

                                    <button
                                        type="button"
                                        className="PlaylistSuggestionsTable__ActionBtn PlaylistSuggestionsTable__ActionBtn--youtube"
                                        title="Buscar pista en YouTube"
                                        onClick={() => OpenYouTubeSearch(song.title, song.artistName)}
                                    >
                                        <i className="bi bi-youtube"></i>
                                    </button>

                                    {/* Botón Editar ✏️ */}
                                    <button
                                        type="button"
                                        className="PlaylistSuggestionsTable__ActionBtn PlaylistSuggestionsTable__ActionBtn--edit"
                                        title="Editar título o artista"
                                        onClick={() => onEdit && onEdit(song)}
                                    >
                                        <i className="bi bi-pencil-fill"></i>
                                    </button>

                                    {/* Botón Marcar Favorita / Transferir a Playlist Activa ⭐ */}
                                    <button
                                        type="button"
                                        className={`PlaylistSuggestionsTable__ActionBtn ${
                                            song.isFavorite
                                                ? 'PlaylistSuggestionsTable__ActionBtn--starActive'
                                                : 'PlaylistSuggestionsTable__ActionBtn--star'
                                        }`}
                                        title={song.isFavorite ? 'Quitar de Playlist Activa' : '⭐ Añadir a Playlist Activa'}
                                        onClick={() => handleToggleFavorite(song)}
                                    >
                                        <i className={`bi ${song.isFavorite ? 'bi-star-fill' : 'bi-star'}`}></i>
                                    </button>

                                    {/* Botón Archivar / Desarchivar 📦 */}
                                    <button
                                        type="button"
                                        className={`PlaylistSuggestionsTable__ActionBtn ${
                                            song.isArchived
                                                ? 'PlaylistSuggestionsTable__ActionBtn--archiveActive'
                                                : 'PlaylistSuggestionsTable__ActionBtn--archive'
                                        }`}
                                        title={song.isArchived ? 'Desarchivar sugerencia' : 'Archivar sugerencia'}
                                        onClick={() => handleToggleArchive(song)}
                                    >
                                        <i className={`bi ${song.isArchived ? 'bi-archive-fill' : 'bi-archive'}`}></i>
                                    </button>

                                    {/* Botón Eliminar 🗑️ */}
                                    <button
                                        type="button"
                                        className="PlaylistSuggestionsTable__ActionBtn PlaylistSuggestionsTable__ActionBtn--delete"
                                        title="Eliminar sugerencia"
                                        onClick={() => handleDelete(song)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Modal Confirmación Genérico */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    setShowModal={setShowConfirm}
                />
            )}
        </section>
    );
};

export default PlaylistSuggestionsTable;
